import { useCallback, useEffect, useState } from "react";
import { runAdAuction, reserveAdImpression, trackAdEvent } from "./useAdsApi";

/* Buyer-side ad slots, following api/BUYER_AD_DELIVERY.md:
   1. POST ads/delivery/auction for the placement + page context
   2. POST ads/delivery/impression for each winner BEFORE rendering it (this is what spends
      the campaign's CPM budget and enforces frequency caps; it rechecks eligibility and can 409)
   3. Only slots whose reservation succeeded are returned, each with its tracking_id
   Placement values must match the API's AD_DELIVERY_PLACEMENTS (configuration/ad-delivery.js):
   "search" needs context.search, "listing" needs context.category_id, "feed" is the homepage
   sidebar and the marketplace "All" tab (context.page tells them apart). */

const DEVICE_TYPE = "web";

const viewerLanguage = () =>
  ((typeof navigator !== "undefined" && navigator.language) || "en").split("-")[0].toLowerCase();

// productOnly: for product-card UIs - winners without a product (e.g. profile ads from "feed")
// are skipped before reservation, so they're never charged without being shown.
export default function useSponsoredAds({ placement, context = {}, count = 1, enabled = true, productOnly = false }) {
  const [ads, setAds] = useState([]);
  // Context is usually an inline object literal - key on its contents, not its identity.
  const contextKey = JSON.stringify(context);

  useEffect(() => {
    // Slots belong to the context they were won for - never carry them over to a new one.
    setAds([]);
    if (!enabled) return undefined;

    let active = true;
    const request = {
      placement,
      device_type: DEVICE_TYPE,
      language: viewerLanguage(),
      context: JSON.parse(contextKey),
    };

    (async () => {
      try {
        const result = await runAdAuction({ ...request, count });
        const slots = (count > 1 ? result?.auctions : [result]) || [];
        // Don't reserve (and spend budget) for a slot the page has already moved away from.
        if (!active) return;

        const reserved = await Promise.all(
          slots
            .filter((slot) => slot?.auction_id && slot.winner && (!productOnly || slot.winner.ad?.product))
            .map(async ({ auction_id: auctionId, winner }) => {
              try {
                const impression = await reserveAdImpression({
                  ...request,
                  auction_id: auctionId,
                  ad_id: winner.ad_id,
                  ad_set_id: winner.ad_set_id,
                  campaign_id: winner.campaign_id,
                });
                // ad_set_id/campaign_id live on the winner, not winner.ad - carry them onto the
                // ad object too so consumers (e.g. the hide-ad preference APIs) can send them.
                return impression?.tracking_id
                  ? {
                      ...winner.ad,
                      trackingId: impression.tracking_id,
                      ad_set_id: winner.ad_set_id,
                      campaign_id: winner.campaign_id,
                    }
                  : null;
              } catch {
                // Expired/ineligible (409) - skip this slot, the page shows ordinary content.
                return null;
              }
            })
        );
        if (active) setAds(reserved.filter(Boolean));
      } catch {
        if (active) setAds([]);
      }
    })();

    return () => {
      active = false;
    };
  }, [placement, contextKey, count, enabled, productOnly]);

  // Fire-and-forget: the API records each event type at most once per impression.
  const trackEvent = useCallback((ad, eventType = "click") => {
    if (!ad?.trackingId) return;
    trackAdEvent({ tracking_id: ad.trackingId, event_type: eventType }).catch(() => {});
  }, []);

  // Drop a slot from this placement's rendered ads right after the viewer hides it, without
  // waiting for a full re-auction.
  const removeAd = useCallback((trackingId) => {
    if (!trackingId) return;
    setAds((prev) => prev.filter((ad) => ad.trackingId !== trackingId));
  }, []);

  return { ads, trackEvent, removeAd };
}
